import { IViewCard, TableColumn } from '@/components';
import { DataGridColSettings } from '@/components/DataGrid/utils';
import { USStatesWithAbbr } from '@/constants/address';
import {
  eyeColorInput,
  hairColorInput,
  languageInput,
  maritalInput,
  phoneNumberInput,
  phoneTypeInput,
} from '@/constants/form_inputs';
import {
  Builds,
  Disabilities,
  EyeDefects,
  Faces,
  FacialFeatures,
  HairTypes,
  SkinTones,
} from '@/constants/users';
import { AgeGroup, OffenderType, OffenderCondition } from '@/models';
import {
  EmergencyContactRelationship,
  OrganizationType,
} from '@/models/master.names';
import {
  formatOrganisationName,
  formatOrganisationType,
  formatPhoneNumber,
  formatPhoneNumberType,
  parsePhoneNumber,
} from '@/utils';

import { FileAcceptedTypes } from '../../../pages/Kases/KaseFiles/components/data';

export type IFieldItem = {
  name: string;
  type: string;
  label: string;
  freeSolo?: boolean;
  clearOnBlur?: boolean;
  addNew?: boolean;
  grid?: { md: number };
  items?: any; // TODO: typecast these
  shouldDisplayCallback?: string;
  rules?: any; // TODO: typecast this also
  multiple?: boolean;
  suffix?: string;
  inputProps?: Record<string, any>;
};
export const wasArrestedItems = [
  {
    label: 'Arrested',
    value: 2,
  },
  {
    label: 'Pending Arrest',
    value: 1,
  },
  {
    label: 'Not Arrested',
    value: 0,
  },
];

export const AgeGroupLabel = {
  [AgeGroup.EXACT_AGE]: 'Exact age',
  [AgeGroup.AGE_RANGE]: 'Age range',
  [AgeGroup.OVER_NINETY_EIGHT_YEARS_OLD]: 'Over 98 Years Old',
  [AgeGroup.UNKNOWN]: 'Unknown',
};

export const OffenderTypeLabel = {
  [OffenderType.SUSPECT]: 'Suspect',
  [OffenderType.ACCOMPLICE]: 'Accomplice',
  [OffenderType.CLEARED]: 'Cleared',
  [OffenderType.CHARGED]: 'Charged',
};

export const OffenderConditionLabel = {
  [OffenderCondition.APPEARS_TO_BE_IMPAIRED_WITH_DRUGS]:
    'Appears to be Impaired with Drugs',
  [OffenderCondition.APPEARS_TO_BE_IMPAIRED_WITH_ALCOHOL]:
    'Appears to be Impaired with Alcohol',
  [OffenderCondition.APPEARS_TO_BE_MENTALLY_DISORDERED]:
    'Appears to be Mentally Disordered',
  [OffenderCondition.APPEARS_TO_BE_INJURED_OR_ILL]:
    'Appears to be Injured or Ill',
  [OffenderCondition.APPARENTLY_NORMAL]: 'Apparently Normal',
  [OffenderCondition.NOT_REPORTED]: 'Not Reported',
  [OffenderCondition.UNKNOWN]: 'Unknown',
};

export const AgeGroupSelectItems = [
  {
    label: AgeGroupLabel[AgeGroup.EXACT_AGE],
    value: AgeGroup.EXACT_AGE,
  },
  {
    label: AgeGroupLabel[AgeGroup.AGE_RANGE],
    value: AgeGroup.AGE_RANGE,
  },
  {
    label: AgeGroupLabel[AgeGroup.OVER_NINETY_EIGHT_YEARS_OLD],
    value: AgeGroup.OVER_NINETY_EIGHT_YEARS_OLD,
  },
  {
    label: AgeGroupLabel[AgeGroup.UNKNOWN],
    value: AgeGroup.UNKNOWN,
  },
];

export const OffenderTypeSelectItems = [
  {
    label: OffenderTypeLabel[OffenderType.SUSPECT],
    value: OffenderType.SUSPECT,
  },
  {
    label: OffenderTypeLabel[OffenderType.ACCOMPLICE],
    value: OffenderType.ACCOMPLICE,
  },
  {
    label: OffenderTypeLabel[OffenderType.CLEARED],
    value: OffenderType.CLEARED,
  },
  {
    label: OffenderTypeLabel[OffenderType.CHARGED],
    value: OffenderType.CHARGED,
  },
];

export const OffenderConditionSelectItems = [
  {
    label: `${OffenderCondition.APPEARS_TO_BE_IMPAIRED_WITH_DRUGS} = ${
      OffenderConditionLabel[
        OffenderCondition.APPEARS_TO_BE_IMPAIRED_WITH_DRUGS
      ]
    }`,
    value: OffenderCondition.APPEARS_TO_BE_IMPAIRED_WITH_DRUGS,
  },
  {
    label: `${OffenderCondition.APPEARS_TO_BE_IMPAIRED_WITH_ALCOHOL} = ${
      OffenderConditionLabel[
        OffenderCondition.APPEARS_TO_BE_IMPAIRED_WITH_ALCOHOL
      ]
    }`,
    value: OffenderCondition.APPEARS_TO_BE_IMPAIRED_WITH_ALCOHOL,
  },
  {
    label: `${OffenderCondition.APPEARS_TO_BE_MENTALLY_DISORDERED} = ${
      OffenderConditionLabel[
        OffenderCondition.APPEARS_TO_BE_MENTALLY_DISORDERED
      ]
    }`,
    value: OffenderCondition.APPEARS_TO_BE_MENTALLY_DISORDERED,
  },
  {
    label: `${OffenderCondition.APPEARS_TO_BE_INJURED_OR_ILL} = ${
      OffenderConditionLabel[OffenderCondition.APPEARS_TO_BE_INJURED_OR_ILL]
    }`,
    value: OffenderCondition.APPEARS_TO_BE_INJURED_OR_ILL,
  },
  {
    label: `${OffenderCondition.APPARENTLY_NORMAL} = ${
      OffenderConditionLabel[OffenderCondition.APPARENTLY_NORMAL]
    }`,
    value: OffenderCondition.APPARENTLY_NORMAL,
  },
  {
    label: `${OffenderCondition.NOT_REPORTED} = ${
      OffenderConditionLabel[OffenderCondition.NOT_REPORTED]
    }`,
    value: OffenderCondition.NOT_REPORTED,
  },
  {
    label: `${OffenderCondition.UNKNOWN} = ${
      OffenderConditionLabel[OffenderCondition.UNKNOWN]
    }`,
    value: OffenderCondition.UNKNOWN,
  },
];

export const TabNamesNextPaths = {
  nameList: [
    'MasterAddName',
    'MasterNameSetInvolvement',
    'MasterNameNotes',
    'MasterNameView',
  ],
  MasterAddName: ['MasterNameView'],
  MasterNameView: ['CFSInvolvementHistory', 'KasesInvolvementHistory'],
  driverLicense: ['MasterAddDriverLicense', 'MasterNameDriverLicenseView'],
  MasterAddNameDetails: [
    'MasterLookupName',
    'MasterNameDriverLicense',
    'driverLicense',
  ],
};

export enum TabNamesScreens {
  CFSInvolvementHistory = 'CFSInvolvementHistory',
  KasesInvolvementHistory = 'KasesInvolvementHistory',
  KasesAddUnknownPerson = 'KasesAddUnknownPerson',
  KasesAddUnknownOrganization = 'KasesAddUnknownOrganization',
  MasterAddDriverLicense = 'MasterAddDriverLicense',
  MasterNameKaseOrganizationForm = 'MasterNameKaseOrganizationForm',
  MasterAddName = 'MasterAddName',
  MasterAddNameDetails = 'MasterAddNameDetails',
  MasterLookupName = 'MasterLookupName',
  MasterNameKaseInvolvement = 'MasterNameKaseInvolvement',
  MasterNameDriverLicense = 'MasterNameDriverLicense',
  MasterNameDriverLicenseView = 'MasterNameDriverLicenseView',
  MasterNameNotes = 'MasterNameNotes',
  MasterNameSetInvolvement = 'MasterNameSetInvolvement',
  MasterNameView = 'MasterNameView',
  NamesList = 'NamesList',
  ViewNamesDetails = 'ViewNamesDetails',
  MasterNameOrganizationView = 'MasterNameOrganizationView',
}

export const KaseNamesTableColumns: TableColumn[] = [
  { key: 'type', label: 'Type' },
  { key: 'name', label: 'Full Name' },
  { key: 'address', label: 'Address' },
  { key: 'involvement', label: 'Involvement' },
  { key: 'isVerified', label: 'Data Source' },
];

export const MasterNamesTableColumns: TableColumn[] = [
  { key: 'name', label: 'Full Name' },
  { key: 'dateOfBirth', label: 'Date of Birth' },
  { key: 'isVerified', label: 'Data Source' },
  { key: 'phone', label: 'Phone Number' },
  { key: 'involvement', label: 'Involvement' },
  { key: 'race', label: 'Race' },
];

export const masterNameDataGridCols: DataGridColSettings[] = [
  {
    field: 'name',
    headerName: 'Full Name',
    width: 200,
    visible: true,
  },
  {
    field: 'type',
    headerName: 'Type',
    width: 150,
    visible: true,
  },
  {
    field: 'dateOfBirth',
    headerName: 'Date of Birth',
    width: 180,
    visible: true,
  },
  {
    field: 'isVerified',
    headerName: 'Data Source',
    width: 180,
    visible: true,
  },
  {
    field: 'phone',
    headerName: 'Phone Number',
    width: 180,
    visible: true,
  },
  {
    field: 'involvement',
    headerName: 'Involvement',
    width: 200,
    visible: true,
  },
  {
    field: 'flags',
    headerName: 'Flags',
    width: 200,
    visible: true,
  },
  {
    field: 'race',
    headerName: 'Race',
    width: 180,
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 180,
    visible: true,
  },
];

export const MasterLookupTableColumns: TableColumn[] = [
  { key: 'name', label: 'Full Name' },
  { key: 'dateOfBirth', label: 'Date of Birth' },
  { key: 'race', label: 'Race' },
  { key: 'ssn', label: 'Social security number' },
  { key: 'gender', label: 'Gender' },
];

export const booleanTypesItems = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

export const setInvolvementNameFormFields = [
  {
    name: 'involvement',
    type: 'autocomplete',
    multiple: true,
    limitTags: 2,
    label: 'Involvement',
    items: 'getInvolvementItems',
  },
  {
    name: 'behavior',
    type: 'autocomplete',
    label: 'Behavior',
    items: 'getBehaviorItems',
  },
];

export const setInvolvementKaseFormFields = [
  {
    name: 'involvement',
    type: 'autocomplete',
    label: 'Involvement',
    multiple: true,
    limitTags: 2,
    items: 'getInvolvementItems',
    rules: {
      required: 'This field is required.',
    },
  },
  {
    name: 'behavior',
    type: 'autocomplete',
    label: 'Behavior',
    items: 'getBehaviorItems',
    disableClearable: true,
  },
  {
    name: 'interviewedBy',
    type: 'autocomplete',
    freeSolo: true,
    clearOnBlur: false,
    addNew: false,
    label: 'Interviewed By',
    items: 'getUsers',
  },
  {
    name: 'wasArrested',
    type: 'autocomplete',
    label: 'Arrest state',
    items: wasArrestedItems,
    disableClearable: true,
    clearOnBlur: false,
    addNew: false,
  },
];

export const involmentContantFields = [
  {
    name: 'ageGroup',
    type: 'select',
    label: 'Age Group',
    items: [
      {
        label: 'Exact age',
        value: 'EXACT_AGE',
      },
      {
        label: 'Age range',
        value: 'AGE_RANGE',
      },
      {
        label: 'Over 98 Years Old',
        value: 'OVER_NINETY_EIGHT_YEARS_OLD',
      },
      {
        label: 'Unknown',
        value: 'UNKNOWN',
      },
    ],
    shouldDisplayCallback: 'checkDobNotSet',
  },
  {
    name: 'age',
    type: 'string',
    label: 'Age',
    shouldDisplayCallback: 'checkExactAge',
    max: 98,
  },
  {
    name: 'ageRange',
    type: 'range',
    label: 'Age range',
    shouldDisplayCallback: 'checkAgeRange',
    defaultValue: [0, 20],
    max: 98,
  },
  {
    name: 'residenceStatus',
    type: 'autocomplete',
    label: 'Residence Status',
    freeSolo: false,
    clearOnBlur: false,
    addNew: false,
  },
  {
    name: 'interviewedBy',
    type: 'autocomplete',
    freeSolo: true,
    clearOnBlur: false,
    addNew: false,
    label: 'Interviewed By',
    items: 'getUsers',
  },
];

export const tabNameNotesTableColumns: TableColumn[] = [
  { key: 'description', label: 'Note description' },
  { key: 'createdBy', label: 'Created by' },
  { key: 'createdAt', label: 'Created on' },
  { key: 'updatedBy', label: 'Last updated by' },
  { key: 'updatedAt', label: 'Last updated on' },
  { key: 'cfs', label: 'Referenced items' },
];

export const masterNameNotesDataGridCols = [
  {
    field: 'description',
    headerName: 'Note description',
    visible: true,
  },
  {
    field: 'createdBy',
    headerName: 'Created by',
    width: 200,
    visible: true,
  },
  {
    field: 'createdAt',
    headerName: 'Date',
    width: 150,
    visible: true,
  },
  {
    field: 'cfs',
    headerName: 'Referenced items',
    width: 150,
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    visible: true,
  },
];

export const OrganizationInvolvementKaseDisabled = ['behavior'];

export const OrganizationInvolvementKaseHidden = [
  'levelOfInjury',
  'injuryType',
  'medicalTreatment',
  'ageGroup',
  'age',
  'residenceStatus',
  'interviewedBy',
  'behavior',
  'wasArrested',
];

export const DriverLicenseFormFields = [
  {
    key: 'licenseInfo',
    title: 'License Info',
    items: [
      {
        name: 'state',
        label: 'State',
        type: 'autocomplete',
        items: USStatesWithAbbr,
      },
      {
        name: 'number',
        label: 'ID number',
        type: 'text',
        rules: {
          maxLength: {
            value: 20,
            message: 'ID number should be less than 20 characters.',
          },
        },
      },
      {
        name: 'vehicleClass',
        type: 'text',
        label: 'Class',
      },
      {
        name: 'restrictionCodes',
        type: 'text',
        label: 'Restriction code(s)',
      },
      {
        name: 'endorsementCodes',
        type: 'text',
        label: 'Endorsement code(s)',
      },
      {
        name: 'expireDate',
        type: 'date',
        label: 'Expiration date',
      },
      {
        name: 'documentNumber',
        label: 'Document number',
        type: 'text',
        rules: {
          maxLength: {
            value: 30,
            message: 'Document number should be less than 30 characters.',
          },
        },
      },
      {
        name: 'issueDate',
        type: 'date',
        label: 'Issue date',
      },
    ],
  },
  {
    key: 'personalInfo',
    title: 'Personal Info',
    items: [
      {
        name: 'address',
        type: 'address',
        label: 'Address',
        grid: {
          md: 8,
          sm: 12,
        },
        rules: {
          required: 'This field is required.',
        },
      },
    ],
  },
];

export const LicenseInfoFormFields = [
  {
    name: 'driverLicense._id',
    type: 'hidden',
  },
  {
    name: 'driverLicense.state',
    label: 'State',
    type: 'autocomplete',
    items: USStatesWithAbbr,
    grid: { md: 3 },
  },
  {
    name: 'driverLicense.number',
    label: 'ID number',
    type: 'text',
    rules: {
      maxLength: {
        value: 20,
        message: 'ID number should be less than 20 characters.',
      },
    },
    grid: { md: 3 },
  },
  {
    name: 'driverLicense.vehicleClass',
    type: 'text',
    label: 'Class',
    grid: { md: 3 },
  },
  {
    name: 'driverLicense.restrictionCodes',
    type: 'text',
    label: 'Restriction code(s)',
    grid: { md: 3 },
  },
  {
    name: 'driverLicense.endorsementCodes',
    type: 'text',
    label: 'Endorsement code(s)',
    grid: { md: 3 },
  },
  {
    name: 'driverLicense.expireDate',
    type: 'date',
    label: 'Expiration date',
    grid: { md: 3 },
  },
  {
    name: 'driverLicense.documentNumber',
    label: 'Document number',
    type: 'text',
    grid: { md: 3 },
  },
  {
    name: 'driverLicense.issueDate',
    type: 'date',
    label: 'Issue date',
    grid: { md: 3 },
  },
];

export const DriverDetailsViewFields = [
  { label: 'Last Name', key: 'lastName' },
  { label: 'Name', key: 'firstName' },
  { label: 'Middle Name', key: 'middleName' },
  { label: 'ID number', key: 'number' },
  { label: 'Date of birth', key: 'birthday' },
  { label: 'State', key: 'state' },
  { label: 'Address', key: 'address.addressLabel' },
  { label: 'City', key: 'address.city' },
  { label: 'State', key: 'address.state' },
  { label: 'Zipcode', key: 'address.zipcode' },
  { label: 'Issue date', key: 'issuedate' },
  { label: 'Expiration date', key: 'expirationDate' },
  { label: 'Gender', key: 'gender' },
  { label: 'Height', key: 'height' },
  { label: 'Eye color', key: 'eyeColor' },
];

export const kasesHistoryTableColumns: TableColumn[] = [
  { key: '_id', label: 'Case ID' },
  { key: 'historyDate', label: 'Date' },
  { key: 'involvement', label: 'Involvement' },
];

export const cfsHistoryTableColumns: TableColumn[] = [
  { key: 'cfs.number', label: 'CFS ID' },
  { key: 'createdAt', label: 'Date created' },
  { key: 'relationship.behavior', label: 'Behavior' },
  { key: 'relationship.involvement', label: 'Involvement' },
];

export type TableRowHistory = {
  _id: string;
  historyDate: string;
  involvement: string;
};

export const kasesHistoryDummyData: TableRowHistory[] = [
  {
    _id: '1',
    historyDate: '10.11.2023',
    involvement: 'Involvement',
  },
  {
    _id: '2',
    historyDate: '10.11.2023',
    involvement: 'Involvement',
  },
  {
    _id: '3',
    historyDate: '10.11.2023',
    involvement: 'Involvement',
  },
  {
    _id: '4',
    historyDate: '10.11.2023',
    involvement: 'Involvement',
  },
];

export const MasterNameViewFields = <Array<IViewCard & { method?: string }>>[
  {
    title: "Driver's license/ID Card",
    key: 'driverLicense',
    size: { md: 2 },
    items: [
      { label: 'State', value: 'driverLicense.state' },
      { label: 'ID Number', value: 'driverLicense.number' },
      { label: 'Class', value: 'driverLicense.vehicleClass' },
      { label: 'Restriction Code(s)', value: 'driverLicense.restrictionCodes' },
      { label: 'Endorsement Code(s)', value: 'driverLicense.endorsementCodes' },
      { label: 'Expiration Date', value: 'driverLicense.expireDate' },
      { label: 'Document Number', value: 'driverLicense.documentNumber' },
      { label: 'Issue Date', value: 'driverLicense.issueDate' },
    ],
  },
  {
    title: 'Identity',
    key: 'Identity',
    size: { md: 2 },
    items: [
      { label: 'Suffix', value: 'suffix' },
      { label: 'Last Name', value: 'lastName' },
      { label: 'First Name', value: 'firstName' },
      { label: 'Middle Name', value: 'middleName' },
      { label: 'Maiden Name', value: 'maidenName' },
      { label: 'Alias Names', value: 'aliasName' },
      { label: 'Nickname', value: 'nickName' },
      { label: 'Date of Birth', value: 'birthday' },
      { label: 'Place of Birth', value: 'placeOfBirth' },
      { label: 'Sex', value: 'sex' },
      { label: 'Race', value: 'race' },
      { label: 'Religion', value: 'religion' },
      { label: 'Education', value: 'education' },
      { label: 'Social Security Number', value: 'ssn' },
      { label: 'NYSID', value: 'nysid' },
      { label: 'FBI Number', value: 'fbiNumber' },
      { label: 'Ref #', value: 'refNumber' },
      { label: 'Nationality', value: 'nationality' },
      { label: 'Citizen of', value: 'citizenOf' },
      { label: 'Ethnic origin', value: 'ethnicOrigin' },
    ],
  },
  {
    title: 'Personal Information',
    key: 'personalInfo',
    size: { md: 2 },
    items: [
      { label: 'Transient', value: 'transient' },
      { label: 'Address', value: 'personalInfo.addresses' },
      {
        label: 'Phone number',
        value: 'personalInfo.phoneNumbers',
      },
    ],
  },
  {
    title: 'Other information',
    key: 'otherInfo',
    size: { md: 2 },
    items: [
      {
        label: 'Marital status',
        value: 'maritalStatus',
      },
      {
        label: 'Languages spoken',
        value: 'spokenLanguages',
      },
      {
        label: 'Social media',
        value: 'socialMedia',
      },
      {
        label: 'Email',
        value: 'emails',
      },
      {
        label: 'Gang affiliations',
        value: 'gangAffiliations',
      },
    ],
  },
  {
    title: 'Emergency Contacts',
    key: 'emergencyContacts',
    size: { md: 2 },
    items: [
      {
        label: 'Last Name',
        value: 'lastName',
      },
      {
        label: 'First Name',
        value: 'firstName',
      },
      {
        label: 'Middle Name',
        value: 'middleName',
      },
      {
        label: 'Relationship',
        value: 'relationship',
      },
      {
        label: 'Phone Number',
        value: 'phoneNumber',
      },
      {
        label: 'Email',
        value: 'email',
      },
      {
        label: 'Address',
        value: 'address',
      },
      {
        label: 'Sub-Address',
        value: 'subAddress',
      },
      {
        label: 'Primary Contact',
        value: 'primaryContact',
      },
    ],
  },
  {
    title: 'Employment information',
    key: 'employerInfo',
    size: { md: 2 },
    items: [
      { label: 'Employer Name', value: 'employerInfo.name' },
      { label: 'Employer Phone Number', value: 'employerInfo.phoneNumber' },
      { label: 'Employer Address', value: 'employerInfo.address.addressLabel' },
      { label: 'Employer Occupation', value: 'employerInfo.occupation' },
    ],
  },

  {
    title: 'Physical Characteristics',
    key: 'physicalCharacteristics',
    size: { md: 2 },
    items: [
      {
        label: 'Weight (lb)',
        value: 'physicalCharacteristics.weight',
      },
      {
        label: 'Height',
        value: 'physicalCharacteristics.heightExact',
      },
      {
        label: 'Build',
        value: 'physicalCharacteristics.build',
      },
      {
        label: 'Skin Tone',
        value: 'physicalCharacteristics.skinTone',
      },
      {
        label: 'Face',
        value: 'physicalCharacteristics.face',
      },
      {
        label: 'Disabilities',
        value: 'physicalCharacteristics.disabilities',
      },
      {
        label: 'Eye Color',
        value: 'physicalCharacteristics.eyeColor',
      },
      {
        label: 'Eye Defects',
        value: 'physicalCharacteristics.eyeDefects',
      },
      {
        label: 'Hair Color',
        value: 'physicalCharacteristics.hairColor',
      },
      {
        label: 'Hair Types',
        value: 'physicalCharacteristics.hairTypes',
      },
      {
        label: 'Facial Features',
        value: 'physicalCharacteristics.facialFeatures',
      },
      {
        label: 'Scars, Marks and Tattoos',
        value: 'physicalCharacteristics.bodyMarking',
        size: {
          md: 3,
        },
      },
    ],
  },
  {
    key: 'verificationStatus',
    title: 'Verification',
    items: [
      {
        label: 'Verification Status',
        value: 'isVerified',
        method: 'checkIsVerified',
      },
    ],
  },
  {
    key: 'photos',
    title: 'Photos',
    size: { md: 2 },
  },
];

export const MasterOrganizationViewFields = <Array<IViewCard>>[
  {
    title: 'Basic information',
    key: 'basicInformation',
    size: { md: 2 },
    items: [
      {
        label: 'Organization Name',
        value: 'name',
        parser: formatOrganisationName,
      },
      {
        label: 'Organization type',
        value: 'organizationType',
        parser: formatOrganisationType,
      },
      {
        label: 'Address',
        value: 'addresses',
        parser: (addresses: any[]) => {
          if (!addresses || addresses.length === 0) return '';
          // Find the active address
          const activeAddr =
            addresses.find((addr) => addr.status === 'ACTIVE') || addresses[0];
          const parts = [
            activeAddr.addressNumber,
            activeAddr.completeStreetName,
            activeAddr.subAddress,
            activeAddr.placeName,
            activeAddr.state,
            activeAddr.zipcode,
            activeAddr.country,
          ].filter(Boolean);
          return parts.join(', ');
        },
      },
    ],
  },
  {
    title: 'Social media links',
    key: 'SocialMediaLinks',
    multiple: 'socialMediaLinks',

    items: [{ label: 'Link' }],
  },
  {
    title: 'Phone numbers',
    key: 'phoneNumbers',
    size: { md: 5 },
    multiple: 'phoneNumbers',
    items: [
      {
        label: 'Phone number',
        value: 'phoneNumber',
        parser: formatPhoneNumber,
      },
      { label: 'Type', value: 'type', parser: formatPhoneNumberType },
    ],
  },
  {
    title: 'Emails',
    key: 'emails',
    multiple: 'emails',
    size: { md: 2 },
    items: [{ label: 'Email' }],
  },
  {
    title: 'Contacts',
    key: 'contacts',
    multiple: 'contacts',
    size: { md: 3 },
    items: [
      { label: 'Name', value: 'name' },
      {
        label: 'Phone number',
        value: 'phoneNumber',
        parser: formatPhoneNumber,
      },
      { label: 'Phone number ext.', value: 'phoneNumberExt' },
    ],
  },
  {
    title: 'Urls',
    key: 'urls',
    multiple: 'urls',

    items: [{ label: 'URL' }],
  },
  {
    key: 'verificationStatus',
    title: 'Verification',
    items: [
      {
        label: 'Verification Status',
        value: 'isVerified',
      },
    ],
  },
  {
    key: 'photos',
    title: 'Photos',
    size: { md: 2 },
  },
];

export const MasterNamePhoneNumberFormFields = [
  {
    ...phoneTypeInput,
    grid: {
      md: 4,
    },
  },
  {
    ...phoneNumberInput,
    name: 'phoneNumber',
    label: 'Phone number',
    rules: {
      ...phoneNumberInput.rules,
    },
    grid: {
      md: 4,
    },
  },
];

export const MasterNameIdentitySupplementalFields: IFieldItem[] = [
  {
    name: 'nysid',
    type: 'text',
    label: 'NYSID',
    grid: {
      md: 3,
    },
    rules: {
      minLength: {
        value: 9,
        message: 'NYSID must be exactly 9 characters.',
      },
      maxLength: {
        value: 9,
        message: 'NYSID must be exactly 9 characters.',
      },
    },
    inputProps: {
      maxLength: 9,
    },
  },
  {
    name: 'fbiNumber',
    type: 'text',
    label: 'FBI Number',
    grid: {
      md: 3,
    },
    rules: {
      minLength: {
        value: 8,
        message: 'FBI Number must be between 8 and 9 characters.',
      },
      maxLength: {
        value: 9,
        message: 'FBI Number must be between 8 and 9 characters.',
      },
    },
    inputProps: {
      maxLength: 9,
    },
  },
  {
    name: 'refNumber',
    type: 'text',
    label: 'Ref #',
    grid: {
      md: 3,
    },
  },
];

export const MasterNameFormFields = [
  {
    key: 'identity',
    title: 'Identity',
    items: [],
    footer: 'renderIdentityFooter',
  },
  {
    key: 'personalInfo',
    title: 'Personal information',
    items: [
      {
        label: 'Transient (Address not required)',
        name: 'transient',
        type: 'checkbox',
        grid: {
          md: 3,
          sm: 12,
        },
        value: false,
      },
      {
        name: 'addresses',
        type: 'addresses',
        label: 'Address',
        grid: {
          md: 9,
          sm: 12,
        },
      },
      {
        name: 'phoneNumbers',
        label: 'Home phone number',
        grid: {
          md: 12,
          sm: 12,
        },
      },
    ],
  },
  {
    title: 'Other information',
    key: 'otherInfo',
    items: [
      maritalInput,
      languageInput,
      {
        type: 'socialMedia',
        name: 'socialMedia',
        grid: {
          md: 12,
        },
      },
      {
        type: 'emails',
        name: 'emails',
        grid: {
          md: 8,
        },
      },
      {
        label: 'Gang Affiliations',
        name: 'gangAffiliations',
        type: 'input',
        placeholder:
          'Enter values, separated by commas (e.g., Gang A, Gang B, Gang C)',
        grid: {
          md: 6.02,
        },
      },
    ],
  },
  {
    title: 'Emergency Contacts',
    key: 'emergencyContacts',
    items: [],
  },
  {
    title: 'Employment information',
    key: 'employerInfo',
    items: [
      {
        name: 'employerInfo.name',
        type: 'text',
        label: 'Employer name',
        rules: {
          maxLength: {
            value: 50,
            message: 'Name should be less than 50 characters.',
          },
        },
        grid: {
          md: 3,
        },
      },
      {
        ...phoneNumberInput,
        name: 'employerInfo.phoneNumber',
        label: 'Employer phone number',
        grid: {
          md: 3,
        },
      },
      {
        name: 'employerInfo.address',
        type: 'address',
        label: 'Address',
        grid: {
          md: 6,
        },
      },
      {
        name: 'employerInfo.occupation',
        label: 'Occupation',
        rules: {
          maxLength: {
            value: 50,
            message: 'Occupation should be less than 50 characters.',
          },
        },
        grid: {
          md: 3,
        },
      },
    ],
  },
  {
    title: 'Physical characteristics',
    key: 'physicalCharacteristics',
    items: [
      {
        name: 'physicalCharacteristics.weight',
        type: 'number',
        hideSpinner: true,
        label: 'Weight',
        suffix: 'lb',
        placeholder: '/',
        grid: {
          md: 2,
        },
        rules: {
          min: {
            value: 0,
            message: 'Weight cannot be negative. Please enter a valid weight.',
          },
        },
      },
      {
        name: 'physicalCharacteristics.heightExact',
        type: 'height-exact',
        label: 'Height (ft/in)',
        grid: {
          md: 2,
        },
      },
      {
        name: 'physicalCharacteristics.build',
        type: 'autocomplete',
        label: 'Build',
        grid: {
          md: 2,
        },
        items: Builds,
      },
      {
        name: 'physicalCharacteristics.skinTone',
        type: 'autocomplete',
        label: 'Skin tone',
        grid: {
          md: 2,
        },
        items: SkinTones,
      },
      {
        name: 'physicalCharacteristics.face',
        type: 'autocomplete',
        multiple: true,
        label: 'Face',
        grid: {
          md: 2,
        },
        items: Faces,
      },
      {
        name: 'physicalCharacteristics.disabilities',
        type: 'autocomplete',
        multiple: true,
        label: 'Disabilities',
        grid: {
          md: 2,
        },
        items: Disabilities,
      },
      {
        ...eyeColorInput,
        name: 'physicalCharacteristics.eyeColor',
        grid: { md: 2 },
      },
      {
        name: 'physicalCharacteristics.eyeDefects',
        type: 'autocomplete',
        multiple: true,
        label: 'Eye Defects',
        grid: {
          md: 4,
        },
        items: EyeDefects,
      },
      {
        ...hairColorInput,
        name: 'physicalCharacteristics.hairColor',
        grid: {
          md: 2,
        },
      },
      {
        name: 'physicalCharacteristics.hairTypes',
        type: 'autocomplete',
        multiple: true,
        label: 'Hair types',
        grid: {
          md: 2,
        },
        items: HairTypes,
      },
      {
        name: 'physicalCharacteristics.facialFeatures',
        type: 'autocomplete',
        multiple: true,
        label: 'Facial features',
        grid: {
          md: 2,
        },
        items: FacialFeatures,
      },
      {
        name: 'physicalCharacteristics.bodyMarking',
        type: 'text',
        label: 'Scars, marks and tattoos',
        placeholder: '/',
        grid: {
          md: 12,
        },
      },
    ],
  },
  {
    key: 'photos',
    title: 'Photos',
    items: [
      {
        name: 'files',
        type: 'file',
        accept: FileAcceptedTypes,
        label: '',
        grid: {
          md: 12,
          lg: 12,
        },
      },
    ],
  },
  {
    key: 'verified',
    title: '',
    items: [
      {
        name: 'isVerified',
        type: 'checkbox',
        label: 'Verified',
        grid: {
          md: 6,
        },
      },
    ],
  },
];

export const EmergencyContactsTableColumns = [
  'Last name',
  'First name',
  'Middle name',
  'Relationship',
  'Phone number',
  'Email',
  'Address',
  'Sub-address',
  'Primary contact',
];

export const InformationTypes = {
  URL: 'urls',
  SOCIAL_MEDIA_LINKS: 'socialMediaLinks',
  CONTACTS: 'contacts',
  PHONE_NUMBERS: 'phoneNumbers',
  EMAILS: 'emails',
} as const;

export type InformationType = keyof typeof InformationTypes;
export type InformationValues = (typeof InformationTypes)[InformationType];
export const informationTypesItems = [
  {
    label: 'choose information type',
    value: '0',
    disabled: true,
  },
  {
    label: 'URL',
    value: InformationTypes.URL,
  },

  {
    label: 'Social Media Links',
    value: InformationTypes.SOCIAL_MEDIA_LINKS,
  },
  {
    label: 'Contact',
    value: InformationTypes.CONTACTS,
  },
  {
    label: 'Phone number',
    value: InformationTypes.PHONE_NUMBERS,
  },
  {
    label: 'Email',
    value: InformationTypes.EMAILS,
  },
];

export const MasterNameKaseOrganizationFields = [
  {
    key: 'basicInformation',
    title: 'Basic information',
    items: [
      {
        name: 'name',
        label: 'Organization name',
        grid: {
          md: 4,
        },
        rules: {
          required: 'This field is required.',
          maxLength: {
            value: 50,
            message:
              'Organization name should be less than 50 characters long.',
          },
        },
      },
      {
        name: 'organizationType',
        label: 'Organization type',
        type: 'autocomplete',
        items: Object.entries(OrganizationType).map(([value, label]) => ({
          label,
          value,
        })),
        grid: {
          md: 4,
        },
      },
      {
        name: 'address',
        type: 'address',
        showGpsIcon: true,
        label: 'Address',
        grid: {
          md: 6,
        },
      },
      {
        name: 'informationTypes',
        label: 'Information type',
        type: 'autocomplete',
        items: informationTypesItems,
        defaultValue: '0',
        grid: {
          md: 12,
        },
      },
    ],
  },
  {
    key: 'photos',
    title: 'Photos',
    items: [
      {
        name: 'files',
        type: 'file',
        accept: FileAcceptedTypes,
        label: '',
        grid: {
          md: 12,
          lg: 12,
        },
      },
    ],
  },
  {
    key: 'verified',
    title: '',
    items: [
      {
        name: 'isVerified',
        type: 'checkbox',
        label: 'Verified',
        grid: {
          md: 6,
        },
      },
    ],
  },
];
export const phoneColumns = [
  'employerInfo.phoneNumber',
  'personalInfo.homePhoneNumber',
  'personalInfo.cellPhoneNumber',
];

export const infoTypeViewFields = [
  {
    title: 'Social media links',
    items: [{ label: 'link' }],
  },
  {
    title: 'Phone numbers',
    items: [
      { label: 'Phone number', value: 'phoneNumber', parser: parsePhoneNumber },
      { label: 'Type', value: 'type' },
    ],
  },
  {
    title: 'Contacts',
    items: [
      { label: 'Name', value: 'name' },
      { label: 'Phone number', value: 'phoneNumber', parser: parsePhoneNumber },
    ],
  },
  {
    title: 'Urls',
    items: [{ label: 'url' }],
  },
];
export const infoTypeMap = {
  [InformationTypes.SOCIAL_MEDIA_LINKS]: {
    addLabel: 'New social media link',
    InputFields: [
      {
        name: 'social_media_link',
        label: 'Social media link',
        rules: {
          pattern: {
            value:
              /^(https?:\/\/)?([a-zA-Z0-9.-]+)(\.[a-zA-Z]{2,})(:[0-9]{1,5})?(\/[^\s]*)?$/i, // value: /[2-9]{1}[0-9]{9}$/im,
            message: 'Invalid social media link.',
          },
        },
      },
    ],
  },
  [InformationTypes.PHONE_NUMBERS]: {
    addLabel: 'New phone number',
    InputFields: [
      { ...phoneTypeInput },
      {
        ...phoneNumberInput,
        name: 'phoneNumber',
        label: 'Phone number',
      },
    ],
  },
  [InformationTypes.CONTACTS]: {
    addLabel: 'New contact',
    InputFields: [
      { name: 'name', label: 'Contact person name' },
      {
        ...phoneNumberInput,
        name: 'phoneNumber',
        label: 'Contact person phone number',
      },
      {
        type: 'pattern',
        name: 'phoneNumberExt',
        label: 'Contact person phone number ext.',
        format: '#####',
      },
    ],
  },
  [InformationTypes.URL]: {
    addLabel: 'New URL',
    InputFields: [
      {
        name: `url`,
        label: 'URL',
        rules: {
          pattern: {
            value:
              /^(https?:\/\/)?([a-zA-Z0-9.-]+)(\.[a-zA-Z]{2,})(:[0-9]{1,5})?(\/[^\s]*)?$/i, // value: /[2-9]{1}[0-9]{9}$/im,
            message: 'Invalid url.',
          },
        },
      },
    ],
  },
  [InformationTypes.EMAILS]: {
    addLabel: 'New email',
    InputFields: [
      {
        name: `email`,
        type: 'email',
        label: 'Email address',
        rules: {
          required: 'This field is required.',
          pattern: {
            value:
              /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/, // eslint-disable-line
            message: 'Invalid email',
          },
        },
      },
    ],
  },
};

export const EmergencyContactViewFields = [
  {
    key: 'lastName',
    label: 'Last Name',
  },
  {
    key: 'firstName',
    label: 'First Name',
  },
  {
    key: 'middleName',
    label: 'Middle Name',
  },
  {
    key: 'addresses',
    label: 'Address',
  },
  {
    key: 'subAddress',
    label: 'Sub-Address',
  },
  {
    key: 'email',
    label: 'Email',
  },
  {
    key: 'phoneNumbers',
    label: 'Phone number',
  },
];

export const EmergencyContactFormFields = [
  {
    name: 'lastName',
    label: 'Last name',
    rules: {
      required: 'This field is required.',
      maxLength: {
        value: 50,
        message: 'Name should be less than 50 characters.',
      },
    },
    isMasterNameField: true,
    grid: {
      md: 12,
    },
  },
  {
    name: 'firstName',
    label: 'First name',
    rules: {
      required: 'This field is required.',
      maxLength: {
        value: 50,
        message: 'Name should be less than 50 characters.',
      },
    },
    isMasterNameField: true,
    grid: {
      md: 12,
    },
  },
  {
    name: 'middleName',
    label: 'Middle name',
    rules: {
      maxLength: {
        value: 50,
        message: 'Name should be less than 50 characters.',
      },
    },
    isMasterNameField: true,
    grid: {
      md: 12,
    },
  },
  {
    label: 'Transient (Address not required)',
    name: 'transient',
    type: 'checkbox',
    isMasterNameField: true,
    grid: {
      md: 4,
      sm: 12,
    },
    value: false,
  },
  {
    name: 'addresses',
    type: 'addresses',
    label: 'Address',
    isMasterNameField: true,
    grid: {
      md: 12,
    },
    rules: {
      validate: (value: any, formValues: any) => {
        if (!formValues?.transient && !value?.addressLabel?.trim()) {
          return 'This field is required';
        }
        return true;
      },
    },
  },
  {
    type: 'emails',
    name: 'emails',
    isMasterNameField: true,
    grid: {
      md: 12,
    },
  },
  {
    name: 'phoneNumbers',
    label: 'Home phone number',
    isMasterNameField: true,
    grid: {
      md: 12,
    },
  },
  {
    type: 'select',
    name: 'relationship',
    label: 'Relationship',
    rules: {
      required: 'This field is required.',
    },
    grid: {
      md: 12,
    },
    items: [
      ...Object.values(EmergencyContactRelationship).map((value) => ({
        label: value,
        value,
      })),
    ],
  },
  {
    label: 'Set as Primary Contact',
    name: 'isPrimary',
    type: 'checkbox',
    grid: {
      md: 12,
    },
    value: false,
  },
];
